'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TeamMember {
  name: string;
  role: string;
  bio: string;
  image: string;
  alt: string;
  linkedin: string;
}

interface TeamSectionProps {
  className?: string;
}

const TeamSection = ({ className = '' }: TeamSectionProps) => {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  const teamMembers: TeamMember[] = [
  {
    name: 'Sarah Chen',
    role: 'CEO & Co-Founder',
    bio: 'Former quantitative analyst at Goldman Sachs with 15 years of experience in algorithmic trading. Sarah leads our vision of democratizing professional trading through AI.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13dd7b4b8-1763293360912.png",
    alt: 'Professional Asian woman with long black hair in navy blazer smiling confidently',
    linkedin: '#'
  },
  {
    name: 'Michael Rodriguez',
    role: 'CTO & Co-Founder',
    bio: 'AI researcher and former lead engineer at DeepMind. Michael architected our proprietary AI engine that powers Signaly\'s 87% confidence rate.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd7409d2-1763295403916.png",
    alt: 'Hispanic man with short dark hair in gray suit with warm smile',
    linkedin: '#'
  },
  {
    name: 'Emily Thompson',
    role: 'Head of Product',
    bio: 'Product strategist with background at Robinhood and Coinbase. Emily ensures our platform remains intuitive and accessible for traders of all levels.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1efea8232-1763295833460.png",
    alt: 'Blonde woman in white blouse with professional headshot in office setting',
    linkedin: '#'
  },
  {
    name: 'David Kim',
    role: 'Head of AI Research',
    bio: 'PhD in Machine Learning from MIT. David leads our research team in developing next-generation predictive models for market analysis.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_131b339b1-1763296128271.png",
    alt: 'Asian man with glasses in dark suit with confident expression',
    linkedin: '#'
  },
  {
    name: 'Jessica Martinez',
    role: 'Head of Community',
    bio: 'Community builder with experience at Discord and Reddit. Jessica fosters our thriving community of 50,000+ traders worldwide.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_173ff152a-1763294925392.png",
    alt: 'Hispanic woman with curly brown hair in teal blazer with friendly smile',
    linkedin: '#'
  },
  {
    name: 'Alex Johnson',
    role: 'Head of Security',
    bio: 'Cybersecurity expert with certifications in SOC 2 compliance. Alex ensures bank-level encryption and data protection for all users.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15d40f4e6-1763293883550.png",
    alt: 'Man with beard in black shirt with serious professional expression',
    linkedin: '#'
  }];


  return (
    <section className={`py-16 lg:py-24 bg-card ${className}`}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-foreground mb-6">
            Meet Our Team
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            A diverse group of experts united by a passion for making professional trading accessible to everyone.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {teamMembers.map((member, index) =>
          <div
            key={index}
            className="bg-background border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 hover:scale-105 cursor-pointer"
            onClick={() => setSelectedMember(member)}>

              <div className="relative h-64 overflow-hidden">
                <AppImage
                src={member.image}
                alt={member.alt}
                className="w-full h-full object-cover" />

              </div>
              <div className="p-6">
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  {member.name}
                </h3>
                <p className="text-sm text-primary mb-4">{member.role}</p>
                <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                  {member.bio}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedMember &&
      <div
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        onClick={() => setSelectedMember(null)}>

          <div
          className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}>

            <div className="relative h-80 overflow-hidden">
              <AppImage
              src={selectedMember.image}
              alt={selectedMember.alt}
              className="w-full h-full object-cover" />

              <button
              onClick={() => setSelectedMember(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-background/90 rounded-full flex items-center justify-center hover:bg-background transition-colors">

                <Icon name="XMarkIcon" size={24} className="text-foreground" />
              </button>
            </div>
            <div className="p-8">
              <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                {selectedMember.name}
              </h3>
              <p className="text-primary mb-6">{selectedMember.role}</p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {selectedMember.bio}
              </p>
              <a
              href={selectedMember.linkedin}
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors">

                <Icon name="LinkIcon" size={20} />
                <span>Connect on LinkedIn</span>
              </a>
            </div>
          </div>
        </div>
      }
    </section>);

};

export default TeamSection;